import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import "../styles/career.css";

/* ---------- Content: edit these ---------- */
const jobs = [];

const experienceOptions = [
  "Less than 1 year",
  "1-2 years",
  "3-5 years",
  "6-10 years",
  "More than 10 years",
];

const values = [
  {
    icon: "eye",
    title: "Full transparency",
    text: "Open, honest communication in everything we do.",
  },
  {
    icon: "flex",
    title: "Flexible and reliable",
    text: "We adapt to what our team and customers need.",
  },
  {
    icon: "team",
    title: "Great team players",
    text: "Problem solvers who help and support each other.",
  },
];

const nextSteps = [
  {
    title: "We review",
    text: "Every application and resume link is read by our team.",
  },
  {
    title: "We get in touch",
    text: "Shortlisted candidates hear from us to arrange a conversation.",
  },
  {
    title: "We keep your details",
    text: "If there is no match today, we contact you when a suitable role opens.",
  },
];

/* ---------- Submit settings: connect your backend here ---------- */
const API_URL = "/api/careers/apply/"; // your endpoint (POST, JSON)
const TIMEOUT_MS = 15000;

const getCookie = (name) => {
  const m = document.cookie.match(`(?:^|; )${name}=([^;]*)`);
  return m ? decodeURIComponent(m[1]) : "";
};

/*
  Sends the form to your API. Resolves on success (2xx).
  Rejects with an error that has: status, data (parsed JSON body), network, timeout.
*/
async function submitApplication(payload) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  const csrf = getCookie("csrftoken"); // needed for Django session / CSRF setups
  if (csrf) headers["X-CSRFToken"] = csrf;

  let res;
  try {
    res = await fetch(API_URL, {
      method: "POST",
      headers,
      body: JSON.stringify(payload),
      signal: controller.signal,
      credentials: "same-origin",
    });
  } catch (e) {
    const err = new Error("Network error");
    err.network = true;
    err.timeout = e && e.name === "AbortError";
    throw err;
  } finally {
    clearTimeout(timer);
  }

  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    /* empty or non-JSON body */
  }
  if (!res.ok) {
    const err = new Error("Request failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

const SERVER_KEYS = {
  position: "position",
  full_name: "name",
  email: "email",
  phone: "phone",
  experience: "experience",
  resume_link: "resume",
};

function serverFieldErrors(data) {
  const out = {};
  if (!data || typeof data !== "object") return out;
  Object.entries(SERVER_KEYS).forEach(([apiKey, key]) => {
    const msg = Array.isArray(data[apiKey]) ? data[apiKey][0] : data[apiKey];
    if (typeof msg === "string") out[key] = msg;
  });
  return out;
}

function errorMessage(err) {
  if (err.timeout)
    return "The request took too long. Please check your connection and try again.";
  if (err.network)
    return "We couldn't reach the server. Please check your internet connection and try again.";
  if (err.status === 429)
    return "Too many attempts. Please wait a few minutes and try again.";
  if (err.status >= 500)
    return "Something went wrong on our side. Please try again in a moment.";
  return "We couldn't send your details. Please check the form and try again.";
}

/* ---------- Icons ---------- */
const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
};
const Icon = {
  arrow: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  crumb: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2.2">
      <path d="M9 6l6 6-6 6" />
    </svg>
  ),
  brief: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <rect x="3" y="7" width="18" height="13" rx="3" />
      <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7M3 13h18" />
    </svg>
  ),
  eye: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  flex: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <path d="M4 7h10M18 7h2M4 17h2M10 17h10" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="8" cy="17" r="2" />
    </svg>
  ),
  team: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0M16 4.8a3.5 3.5 0 0 1 0 6.4M18 14.2a6.5 6.5 0 0 1 3.5 5.8" />
    </svg>
  ),
};

/* ---------- Validation ---------- */
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const isUrl = (v) => {
  try {
    const u = new URL(v);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch (e) {
    return false;
  }
};

function validate(v, applying) {
  const errors = {};
  if (applying && !v.position) errors.position = "Please choose a position.";
  if (v.name.trim().length < 2) errors.name = "Please enter your full name.";
  if (!emailRe.test(v.email.trim()))
    errors.email = "Please enter a valid email address.";
  const digits = v.phone.replace(/\D/g, "");
  if (
    !/^[+()\-\s\d]+$/.test(v.phone.trim()) ||
    digits.length < 7 ||
    digits.length > 15
  ) {
    errors.phone = "Please enter a valid phone number.";
  }
  if (!v.experience) errors.experience = "Please select your experience.";
  if (!isUrl(v.resume.trim()))
    errors.resume =
      "Please paste a valid link, for example a Google Drive share link.";
  return errors;
}

const emptyValues = {
  position: "",
  name: "",
  email: "",
  phone: "",
  experience: "",
  resume: "",
  company: "",
};

function Field({ id, label, error, hint, full, className = "", children }) {
  return (
    <div
      className={`gt9-field${full ? " gt9-field--full" : ""}${error ? " has-error" : ""} ${className}`}
    >
      <label htmlFor={id}>
        {label} <span>*</span>
      </label>
      {children}
      {hint && !error && (
        <p className="gt9-hint" id={`hint-${id}`}>
          {hint}
        </p>
      )}
      {error && (
        <p className="gt9-error" id={`err-${id}`}>
          {error}
        </p>
      )}
    </div>
  );
}

export default function Careers() {
  const [captchaToken, setCaptchaToken] = useState(null);
  const [applying, setApplying] = useState(false); // true once someone clicks "Apply now"
  const [values_, setValues] = useState(emptyValues);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null); // { type: "success" | "error", text }

  const applyRef = useRef(null);
  const nameRef = useRef(null);
  const noticeRef = useRef(null);
  const captchaRef = useRef(null);
  const refs = {
    position: useRef(null),
    name: nameRef,
    email: useRef(null),
    phone: useRef(null),
    experience: useRef(null),
    resume: useRef(null),
  };

  const jobById = (id) => jobs.find((j) => j.id === id);
  const selectedJob = applying ? jobById(values_.position) : null;

  const set = (key) => (e) => {
    const value = e.target.value;
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
    setNotice(null);
  };

  const startApply = (id) => {
    setApplying(true);
    setValues((prev) => ({ ...prev, position: id }));
    setErrors((prev) => ({ ...prev, position: undefined }));
    setNotice(null);
    applyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => nameRef.current?.focus({ preventScroll: true }), 650);
  };

  const stopApply = () => {
    setApplying(false);
    setValues((prev) => ({ ...prev, position: "" }));
    setErrors((prev) => ({ ...prev, position: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setNotice(null);

    if (values_.company) {
      // honeypot field: only bots fill it in, so pretend it worked
      setValues(emptyValues);
      setNotice({ type: "success", text: "Your details have been submitted." });
      return;
    }

    const found = validate(values_, applying);
    setErrors(found);
    const firstBad = Object.keys(refs).find((k) => found[k]);
    if (firstBad) {
      refs[firstBad].current?.focus();
      return;
    }

    const job = applying ? jobById(values_.position) : null;
    setSending(true);
    try {
      await submitApplication({
        application_type: job ? "position" : "future_openings",
        position: job ? job.title : null,
        full_name: values_.name.trim(),
        email: values_.email.trim().toLowerCase(),
        phone: values_.phone.trim(),
        experience: values_.experience,
        resume_link: values_.resume.trim(),
      });
      setNotice({
        type: "success",
        text: job
          ? `Your application for ${job.title} has been submitted. We will contact you if your profile is shortlisted.`
          : "Your details have been submitted. We will reach out when a suitable role opens up.",
      });
      setValues(emptyValues);
      setErrors({});
      setApplying(false);
    } catch (err) {
      const fieldErrors = err.status === 400 ? serverFieldErrors(err.data) : {};
      const hasFieldErrors = Object.keys(fieldErrors).length > 0;
      if (hasFieldErrors) {
        setErrors(fieldErrors);
        refs[Object.keys(refs).find((k) => fieldErrors[k])]?.current?.focus();
      }
      setNotice({
        type: "error",
        text: hasFieldErrors
          ? "Please fix the highlighted fields and try again."
          : errorMessage(err),
      });
    } finally {
      setSending(false);
      setTimeout(
        () =>
          noticeRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          }),
        50,
      );
    }
  };

  return (
    <main className="gt9-car">
      {/* Hero */}
      <section className="gt9-car__hero">
        <div className="gt9-container">
          <ol className="gt9-crumbs" aria-label="Breadcrumb">
            <li>
              <Link to="/">Home</Link>
              {Icon.crumb}
            </li>
            <li aria-current="page">Careers</li>
          </ol>
          <span className="gt9-eyebrow">Careers</span>
          <h1 className="gt9-car__title">
            Build your career with <span className="hl">Global Tech9 LLC</span>
          </h1>
          <p className="gt9-car__lead">
            We are problem solvers who value transparency, flexibility and great
            teamwork. See where you could fit in.
          </p>
          <a href="#positions" className="gt9-btn gt9-btn--gold">
            View open positions {Icon.arrow}
          </a>
        </div>
      </section>

      <div className="gt9-car__body">
        <div className="gt9-container">
          <div className="gt9-values">
            {values.map((v) => (
              <div className="gt9-value" key={v.title}>
                <i>{Icon[v.icon]}</i>
                <div>
                  <b>{v.title}</b>
                  <p>{v.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Open positions */}
          <section
            className="gt9-pos"
            id="positions"
            aria-labelledby="gt9-pos-title"
          >
            <div className="gt9-pos__head">
              <span className="gt9-eyebrow gt9-eyebrow--dark">
                Open positions
              </span>
              <h2 className="gt9-pos__title" id="gt9-pos-title">
                Become a part of our big family to inspire and get inspired by{" "}
                <span className="hl">professional experts.</span>
              </h2>
            </div>

            {jobs.length === 0 ? (
              <div className="gt9-nojobs">
                <p style={{ margin: 0 }}>
                  There are no open positions right now. Send your details below
                  and we will contact you when one opens.
                </p>
              </div>
            ) : (
              <>
                <p className="gt9-count">
                  <b>{jobs.length}</b> open{" "}
                  {jobs.length === 1 ? "position" : "positions"}
                </p>
                <div className="gt9-jobs">
                  {jobs.map((j) => {
                    const on = applying && values_.position === j.id;
                    return (
                      <article
                        key={j.id}
                        className={`gt9-job${on ? " is-selected" : ""}`}
                      >
                        <div>
                          <h3>{j.title}</h3>
                          <span className="gt9-chip">
                            {Icon.brief}
                            {j.type}
                          </span>
                        </div>
                        <p>{j.text}</p>
                        <button
                          type="button"
                          className="gt9-btn gt9-btn--navy"
                          aria-label={`Apply for ${j.title}`}
                          onClick={() => startApply(j.id)}
                        >
                          <span>{on ? "Selected" : "Apply now"}</span>
                          {Icon.arrow}
                        </button>
                      </article>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          {/* Application form: same form for a specific role and for future openings */}
          <section
            className="gt9-apply"
            id="apply"
            ref={applyRef}
            aria-labelledby="gt9-apply-title"
          >
            <div className="gt9-apply__grid">
              <aside className="gt9-career-panel">
                <span className="gt9-eyebrow">
                  {applying ? "Apply now" : "Future openings"}
                </span>
                <h2 id="gt9-apply-title">
                  {applying
                    ? selectedJob
                      ? `Apply for ${selectedJob.title}`
                      : "Apply for a position"
                    : "Didn't find the position you're looking for?"}
                </h2>
                <p>
                  {applying
                    ? "Fill in the details below. We read every application and contact shortlisted candidates."
                    : "Tell us about yourself and share your resume link. We will keep your details on file and reach out when a suitable role opens up."}
                </p>
                {applying && (
                  <button
                    type="button"
                    className="gt9-switch"
                    onClick={stopApply}
                  >
                    Not this role? Send your details for future openings
                  </button>
                )}
                <ol className="gt9-next">
                  {nextSteps.map((s, i) => (
                    <li key={s.title}>
                      <i>{i + 1}</i>
                      <div>
                        <b>{s.title}</b>
                        {s.text}
                      </div>
                    </li>
                  ))}
                </ol>
              </aside>

              <form className="gt9-form" onSubmit={onSubmit} noValidate>
                <div className="gt9-form__grid">
                  {/* Position dropdown only appears when someone clicked "Apply now" */}
                  {applying && (
                    <Field
                      id="position"
                      label="Position"
                      error={errors.position}
                      full
                      className="gt9-field--pos"
                    >
                      <select
                        id="position"
                        ref={refs.position}
                        value={values_.position}
                        onChange={set("position")}
                        aria-invalid={errors.position ? "true" : undefined}
                      >
                        <option value="">Select a position</option>
                        {jobs.map((j) => (
                          <option key={j.id} value={j.id}>
                            {j.title}
                          </option>
                        ))}
                      </select>
                    </Field>
                  )}
                  <Field id="name" label="Full name" error={errors.name}>
                    <input
                      id="name"
                      ref={refs.name}
                      type="text"
                      autoComplete="name"
                      placeholder="Your full name"
                      value={values_.name}
                      onChange={set("name")}
                      aria-invalid={errors.name ? "true" : undefined}
                    />
                  </Field>
                  <Field id="email" label="Email address" error={errors.email}>
                    <input
                      id="email"
                      ref={refs.email}
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={values_.email}
                      onChange={set("email")}
                      aria-invalid={errors.email ? "true" : undefined}
                    />
                  </Field>
                  <Field id="phone" label="Phone number" error={errors.phone}>
                    <input
                      id="phone"
                      ref={refs.phone}
                      type="tel"
                      autoComplete="tel"
                      placeholder=""
                      value={values_.phone}
                      onChange={set("phone")}
                      aria-invalid={errors.phone ? "true" : undefined}
                    />
                  </Field>
                  <Field
                    id="experience"
                    label="Years of experience"
                    error={errors.experience}
                  >
                    <select
                      id="experience"
                      ref={refs.experience}
                      value={values_.experience}
                      onChange={set("experience")}
                      aria-invalid={errors.experience ? "true" : undefined}
                    >
                      <option value="">Select experience</option>
                      {experienceOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field
                    id="resume"
                    label="Resume link"
                    error={errors.resume}
                    full
                    hint="Upload your resume to Google Drive, set sharing to “Anyone with the link”, then paste the link here."
                  >
                    <input
                      id="resume"
                      ref={refs.resume}
                      type="url"
                      inputMode="url"
                      placeholder="https://drive.google.com/file/d/..."
                      value={values_.resume}
                      onChange={set("resume")}
                      aria-invalid={errors.resume ? "true" : undefined}
                    />
                  </Field>
                  {/* honeypot */}
                  <div className="gt9-hp" aria-hidden="true">
                    <label>
                      Company
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={values_.company}
                        onChange={set("company")}
                      />
                    </label>
                  </div>
                </div>

                <div className="gt9-captcha">
                  <HCaptcha
                    ref={captchaRef}
                    sitekey={process.env.REACT_APP_HCAPTCHA_KEY}
                    reCaptchaCompat={false}
                    onVerify={(token) => {
                      setCaptchaToken(token);
                      setNotice(null);
                    }}
                    onExpire={() => setCaptchaToken(null)}
                    onError={() => setCaptchaToken(null)}
                  />
                </div>

                {/* success / error message */}
                {notice && (
                  <div
                    ref={noticeRef}
                    className={`gt9-alert gt9-alert--${notice.type}`}
                    role={notice.type === "error" ? "alert" : "status"}
                  >
                    {notice.text}
                  </div>
                )}

                <div className="gt9-form__foot">
                  <p>
                    By submitting, you agree that Global Tech9 LLC may store your
                    details to contact you about opportunities.
                  </p>
                  <button
                    type="submit"
                    className="gt9-btn gt9-btn--gold"
                    disabled={sending}
                  >
                    {sending ? (
                      <>
                        <span className="gt9-spin" aria-hidden="true" />{" "}
                        Sending…
                      </>
                    ) : (
                      <>
                        {applying ? "Submit application" : "Send my details"}{" "}
                        {Icon.arrow}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
